import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Check,
  Clock,
  AlertCircle,
  Calendar,
  MoreVertical,
  Edit2,
  Trash2,
  Copy,
  MessageSquare,
  UserCircle,
} from 'lucide-react';
import { AssigneeAvatar } from '@/components/tasks/AssigneeAvatar';
import { RichContent } from '@/components/ui/RichContent';
import { TaskContextMenu } from '@/components/tasks/TaskContextMenu';
import { AssigneePicker } from '@/components/tasks/pickers/AssigneePicker';
import { PriorityPicker } from '@/components/tasks/pickers/PriorityPicker';
import { DueDatePicker } from '@/components/tasks/pickers/DueDatePicker';
import { StartDatePicker } from '@/components/tasks/pickers/StartDatePicker';
import { StatusPicker, StatusConfig } from '@/components/tasks/pickers/StatusPicker';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { cn } from '@/lib/utils';

export interface TaskItemData {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  startDate?: string;
  assignee?: { id: string; name: string; avatarUrl?: string };
  assigneeId?: string;
  assignees?: Array<{ id: string; name: string; avatarUrl?: string }>;
  _count?: { comments?: number };
  // Additional context for global tasks view
  projectId?: string;
  projectName?: string;
  taskListId?: string;
  taskListName?: string;
  // Task status config
  statusConfig?: { id: string; name: string; color: string; isClosed: boolean };
}

interface TaskItemProps {
  task: TaskItemData;
  currentUserId?: string;
  /** Called when the task row is clicked (e.g., to open detail modal) */
  onClick?: () => void;
  /** Called when the toggle checkbox is clicked */
  onToggleComplete?: () => void;
  /** Called when the In Progress button is clicked */
  onToggleInProgress?: () => void;
  /** Called when the Edit action is clicked */
  onEdit?: () => void;
  /** Called when the Delete action is clicked */
  onDelete?: () => void;
  /** Called when the Duplicate action is clicked */
  onDuplicate?: () => void;
  /** Whether toggle is in pending state */
  isToggling?: boolean;
  /** Whether status update is in pending state */
  isUpdatingStatus?: boolean;
  /** Show project and task list info (for global tasks view) */
  showProjectInfo?: boolean;
  /** Use compact layout (smaller padding, inline metadata) */
  compact?: boolean;
  /** Use single-row column layout (title + fixed-width chip columns). Mirrors
   *  the ProjectTaskLists row design. Overrides `compact` when true. */
  columns?: boolean;
  /** Custom class name */
  className?: string;
  /** Highlight as newly created (orange border, scroll into view) */
  isNew?: boolean;
  /** Called when the new-task highlight period ends */
  onNewHighlightEnd?: () => void;
  /** Inline-edit callbacks (only applied in compact mode). When provided,
   *  the relevant chip becomes clickable/editable. */
  onUpdateAssignee?: (assigneeId: string | null) => void;
  onUpdateAssignees?: (assigneeIds: string[]) => void;
  onUpdateStartDate?: (startDate: string | null) => void;
  onUpdateDueDate?: (dueDate: string | null) => void;
  onUpdatePriority?: (priority: string) => void;
  onUpdateStatus?: (statusId: string) => void;
  projectMembers?: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  statusConfigs?: StatusConfig[];
  /** Drag handle element rendered at the start of the row (compact only). */
  dragHandle?: React.ReactNode;
  /** Bulk-selection mode: the whole row becomes a click-to-select target. */
  selectionMode?: boolean;
  /** Whether this row is currently selected. */
  selected?: boolean;
  /** Called when the row is clicked in selection mode (shiftKey extends a range). */
  onSelectChange?: (checked: boolean, shiftKey: boolean) => void;
}

const priorityConfig = {
  LOW: { color: 'text-grey-500', bg: 'bg-grey-300', label: 'Low' },
  MEDIUM: { color: 'text-blue-500', bg: 'bg-accent', label: 'Medium' },
  HIGH: { color: 'text-orange-500', bg: 'bg-warning', label: 'High' },
  URGENT: { color: 'text-error', bg: 'bg-error', label: 'Urgent' },
};

function TaskActions({
  onEdit,
  onDelete,
  onDuplicate,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!onEdit && !onDelete && !onDuplicate) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="p-1 hover:bg-grey-100 rounded transition-colors opacity-0 group-hover:opacity-100"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <MoreVertical className="w-4 h-4 text-grey-400" />
      </button>

      {open && (
        <div className="absolute right-0 bottom-full mb-1 w-40 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
          {onDuplicate && (
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
          )}
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function TaskItem({
  task,
  currentUserId,
  onClick,
  onToggleComplete,
  onToggleInProgress,
  onEdit,
  onDelete,
  onDuplicate,
  isToggling = false,
  isUpdatingStatus = false,
  showProjectInfo = false,
  compact = false,
  columns = false,
  className,
  isNew,
  onNewHighlightEnd,
  onUpdateAssignee,
  onUpdateAssignees,
  onUpdateStartDate,
  onUpdateDueDate,
  onUpdatePriority,
  onUpdateStatus,
  projectMembers,
  statusConfigs,
  dragHandle,
  selectionMode = false,
  selected = false,
  onSelectChange,
}: TaskItemProps) {
  // Staff on this task's project: Solid anywhere, a partner agency on its own.
  // The global tasks view carries the project on the task; project pages on the route.
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const { isProjectStaff: isAgencyMember } = useProjectRoles(task.projectId ?? routeProjectId);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const handleContextMenu = (e: React.MouseEvent) => {
    if (onDuplicate || onEdit || onDelete) {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };
  const isCompleted = task.statusConfig?.isClosed ?? task.status === 'COMPLETED';
  const isInProgress = task.status === 'IN_PROGRESS';
  const isAssignee = currentUserId && (task.assigneeId === currentUserId || task.assignee?.id === currentUserId);
  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;
  // Compare dates in UTC to avoid timezone issues
  const isOverdue = task.dueDate && (() => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    // Compare UTC dates only (ignore time)
    const dueDateUTC = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    return dueDateUTC < todayUTC;
  })() && !isCompleted;
  const commentsCount = task._count?.comments || 0;

  const elementRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isNew && elementRef.current) {
      elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const timer = setTimeout(() => onNewHighlightEnd?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  // Bulk-selection mode: the whole row is the click target (no checkbox).
  // Clicking anywhere toggles selection; shift-click extends a range. Every
  // inner control (complete circle, pickers, drag handle) is made inert via
  // `pointer-events-none` so those clicks fall through to the row and select.
  const rowClick = selectionMode
    ? (e: React.MouseEvent) => onSelectChange?.(!selected, e.shiftKey)
    : onClick;
  const selectModeClasses = selectionMode
    ? 'cursor-pointer select-none [&_button]:pointer-events-none [&_input]:pointer-events-none [&_a]:pointer-events-none [&_[role=button]]:pointer-events-none'
    : '';
  const selectedClasses = selectionMode && selected ? 'ring-2 ring-accent border-accent bg-accent/5' : '';

  // Simplified view for completed tasks - only show title
  if (isCompleted) {
    return (
      <div
        onClick={rowClick}
        className={cn(
          'group flex items-center gap-2 rounded-lg transition-all',
          compact ? 'p-2 -mx-2 hover:bg-grey-50' : 'p-3 border bg-grey-50 border-grey-100 opacity-75',
          (onClick || selectionMode) && 'cursor-pointer',
          !compact && onClick && 'hover:opacity-100',
          selectModeClasses,
          selectedClasses,
          className
        )}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete?.();
          }}
          disabled={isToggling}
          className={cn(
            'flex-shrink-0 rounded-full bg-success border-success text-white flex items-center justify-center',
            compact ? 'w-4 h-4' : 'w-5 h-5'
          )}
        >
          <Check className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        </button>
        <p className={cn(
          'flex-1 text-grey-400 line-through truncate',
          compact ? 'text-sm' : 'text-sm'
        )}>
          {task.title}
        </p>
      </div>
    );
  }

  // Columns layout — single-row with fixed-width chip columns, mirrors
  // the task-list-overview (`ProjectTaskLists`) row design.
  if (columns) {
    const canEditAssignee = (!!onUpdateAssignee || !!onUpdateAssignees) && !!projectMembers;
    const canEditPriority = !!onUpdatePriority;
    const canEditDueDate = !!onUpdateDueDate;
    const canEditStatus = !!onUpdateStatus && !!statusConfigs;

    return (
      <div
        ref={elementRef}
        onClick={rowClick}
        onContextMenu={selectionMode ? undefined : handleContextMenu}
        className={cn(
          'group flex items-center gap-2 p-3 rounded-lg border border-grey-200 hover:border-grey-300 transition-all',
          isOverdue ? 'bg-error/5' : 'bg-white',
          (onClick || selectionMode) && 'cursor-pointer',
          isNew && 'border-2 border-orange-400',
          selectModeClasses,
          selectedClasses,
          className,
        )}
      >
        {!selectionMode && dragHandle}

        {/* Complete circle — inert (pointer-events-none) while selecting. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete?.();
          }}
          disabled={isToggling || !onToggleComplete}
          className={cn(
            'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
            isCompleted
              ? 'bg-success border-success text-white'
              : onToggleComplete
                ? 'border-grey-300 hover:border-primary cursor-pointer'
                : 'border-grey-200 cursor-not-allowed opacity-50',
            isToggling && 'opacity-50',
          )}
        >
          {isCompleted && <Check className="w-3 h-3" />}
        </button>

        {/* Title + comments count */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <p
            className={cn(
              'text-sm font-medium truncate',
              isCompleted ? 'text-grey-400' : isOverdue ? 'text-error' : 'text-secondary',
            )}
          >
            {task.title}
          </p>
          {commentsCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-grey-400 shrink-0">
              <MessageSquare className="w-3 h-3" />
              {commentsCount}
            </div>
          )}
        </div>

        {/* Assignee column */}
        <div className="w-[100px] flex-shrink-0 flex justify-center">
          {canEditAssignee ? (
            onUpdateAssignees ? (
              <AssigneePicker
                assignees={task.assignees ?? (task.assignee ? [task.assignee] : [])}
                projectMembers={projectMembers!}
                onChangeMultiple={onUpdateAssignees}
                stopDragPointerDown={false}
              />
            ) : (
              <AssigneePicker
                assignee={task.assignee}
                assigneeId={task.assigneeId}
                projectMembers={projectMembers!}
                onChange={onUpdateAssignee!}
                stopDragPointerDown={false}
              />
            )
          ) : (task.assignees?.length || task.assignee) ? (
            <div className="flex items-center -space-x-1.5">
              {(task.assignees ?? (task.assignee ? [task.assignee] : [])).slice(0, 3).map((a) => (
                <AssigneeAvatar key={a.id} person={a} className="ring-1 ring-white" />
              ))}
            </div>
          ) : (
            <UserCircle className="w-5 h-5 text-grey-300" />
          )}
        </div>

        {/* Status column */}
        <div className="w-[110px] flex-shrink-0 flex justify-center">
          {canEditStatus ? (
            <StatusPicker
              statusConfig={task.statusConfig ?? null}
              statusConfigs={statusConfigs!}
              onChange={onUpdateStatus}
              stopDragPointerDown={false}
            />
          ) : task.statusConfig ? (
            <StatusPicker
              statusConfig={task.statusConfig}
              statusConfigs={statusConfigs ?? []}
            />
          ) : null}
        </div>

        {/* Priority column */}
        <div className="w-[90px] flex-shrink-0 flex justify-center">
          <PriorityPicker
            priority={task.priority}
            onChange={canEditPriority ? onUpdatePriority : undefined}
            stopDragPointerDown={false}
          />
        </div>

        {/* Start date column — agency only */}
        {isAgencyMember && (
          <div className="w-[100px] flex-shrink-0 flex justify-center">
            <StartDatePicker
              startDate={task.startDate}
              onChange={onUpdateStartDate}
              stopDragPointerDown={false}
            />
          </div>
        )}

        {/* Due date column */}
        <div className="w-[100px] flex-shrink-0 flex justify-center">
          {canEditDueDate ? (
            <DueDatePicker
              dueDate={task.dueDate}
              onChange={onUpdateDueDate}
              isOverdue={!!isOverdue}
              isCompleted={isCompleted}
              stopDragPointerDown={false}
            />
          ) : task.dueDate ? (
            <span
              className={cn(
                'flex items-center gap-1 text-xs',
                isOverdue ? 'text-error' : 'text-grey-500',
              )}
            >
              {isOverdue ? (
                <AlertCircle className="w-3 h-3" />
              ) : (
                <Calendar className="w-3 h-3" />
              )}
              {new Date(task.dueDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          ) : null}
        </div>

        {/* Actions menu */}
        <TaskActions onEdit={onEdit} onDelete={onDelete} onDuplicate={onDuplicate} />
        {contextMenu && (
          <TaskContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onClose={() => setContextMenu(null)}
          />
        )}
      </div>
    );
  }

  // Compact layout - inline, minimal padding
  if (compact) {
    const canEditAssignee = (!!onUpdateAssignee || !!onUpdateAssignees) && !!projectMembers;
    const canEditPriority = !!onUpdatePriority;
    const canEditDueDate = !!onUpdateDueDate;
    const canEditStatus = !!onUpdateStatus && !!statusConfigs;
    return (
      <div
        ref={elementRef}
        onClick={onClick}
        onContextMenu={handleContextMenu}
        className={cn(
          'group flex items-start gap-3 p-2 -mx-2 rounded-lg hover:bg-grey-50 transition-colors',
          isOverdue && 'bg-error/5',
          onClick && 'cursor-pointer',
          isNew && 'border-2 border-orange-400',
          className
        )}
      >
        {dragHandle}
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete?.();
          }}
          disabled={isToggling}
          className={cn(
            'mt-0.5 flex-shrink-0 transition-colors',
            isToggling && 'opacity-50',
            isInProgress ? 'text-accent hover:text-success' : 'text-grey-300 hover:text-success'
          )}
        >
          {isInProgress ? (
            <Clock className="w-4 h-4" />
          ) : (
            <div className="w-4 h-4 rounded-full border-2 border-current" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-sm hover:text-accent transition-colors truncate',
                isOverdue ? 'text-error' : 'text-secondary'
              )}
            >
              {task.title}
            </span>
          </div>

          {/* Metadata row */}
          {/* WRAPS on a phone. This row carries list name, assignees, due date
              and status; in one nowrap line the widest task set the page width
              (448px in a 390px viewport, mobile audit 2026-08-28). min-w-0 so
              its own children may truncate. */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs mt-0.5">
            {/* Project and task list info */}
            {showProjectInfo && task.projectName ? (
              <span className="text-grey-400">
                {task.projectName}
                {task.taskListName && ` · ${task.taskListName}`}
              </span>
            ) : task.taskListName ? (
              <span className="text-grey-400">{task.taskListName}</span>
            ) : null}

            {/* Assignee — clickable picker when editable */}
            {canEditAssignee ? (
              onUpdateAssignees ? (
                <AssigneePicker
                  assignees={task.assignees ?? (task.assignee ? [task.assignee] : [])}
                  projectMembers={projectMembers!}
                  onChangeMultiple={onUpdateAssignees}
                  stopDragPointerDown={false}
                />
              ) : (
                <AssigneePicker
                  assignee={task.assignee}
                  assigneeId={task.assigneeId}
                  projectMembers={projectMembers!}
                  onChange={onUpdateAssignee!}
                  stopDragPointerDown={false}
                />
              )
            ) : (task.assignees?.length || task.assignee) ? (
              (() => {
                const people = task.assignees?.length
                  ? task.assignees
                  : task.assignee
                    ? [task.assignee]
                    : [];
                return (
                  <span className="flex items-center gap-1 text-grey-500">
                    <span className="flex items-center -space-x-1.5">
                      {people.slice(0, 3).map((a) => (
                        <AssigneeAvatar key={a.id} person={a} className="w-4 h-4 ring-1 ring-white" />
                      ))}
                    </span>
                    <span className="truncate max-w-[80px]">
                      {people.length === 1 ? people[0].name : `${people.length} assignees`}
                    </span>
                  </span>
                );
              })()
            ) : (
              <span className="text-grey-400 italic">No assignee</span>
            )}

            {/* Priority — flag chip (read-only or editable) */}
            {task.priority && (
              <PriorityPicker
                priority={task.priority}
                onChange={canEditPriority ? onUpdatePriority : undefined}
                stopDragPointerDown={false}
              />
            )}

            {/* Status — chip. Falls back to In Progress toggle when no status picker. */}
            {canEditStatus ? (
              <StatusPicker
                statusConfig={task.statusConfig ?? null}
                statusConfigs={statusConfigs!}
                onChange={onUpdateStatus}
                stopDragPointerDown={false}
              />
            ) : (
              <>
                {/* In Progress toggle - only for assignees */}
                {isAssignee && onToggleInProgress && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleInProgress();
                    }}
                    disabled={isUpdatingStatus}
                    className={cn(
                      'flex items-center gap-1 px-1.5 py-0.5 rounded-full transition-colors',
                      isInProgress
                        ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        : 'bg-grey-100 text-grey-500 hover:bg-grey-200',
                      isUpdatingStatus && 'opacity-50'
                    )}
                  >
                    <Clock className="w-3 h-3" />
                    {isInProgress ? 'In Progress' : 'Start'}
                  </button>
                )}

                {/* Show status badge for non-assignees when in progress */}
                {!isAssignee && isInProgress && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-500">
                    <Clock className="w-3 h-3" />
                    In Progress
                  </span>
                )}
              </>
            )}

            {/* Comments count */}
            {commentsCount > 0 && (
              <span className="flex items-center gap-1 text-grey-400">
                <MessageSquare className="w-3 h-3" />
                {commentsCount}
              </span>
            )}

            {/* Spacer to push due date right */}
            <div className="flex-1 min-w-[8px]" />

            {/* Start date — agency only */}
            {isAgencyMember && (
              <StartDatePicker
                startDate={task.startDate}
                onChange={onUpdateStartDate}
                stopDragPointerDown={false}
              />
            )}

            {/* Due date — clickable picker when editable, display otherwise */}
            {canEditDueDate ? (
              <DueDatePicker
                dueDate={task.dueDate}
                onChange={onUpdateDueDate}
                isOverdue={!!isOverdue}
                isCompleted={isCompleted}
                stopDragPointerDown={false}
              />
            ) : task.dueDate ? (
              <span className={cn(
                'flex items-center gap-1 flex-shrink-0',
                isOverdue ? 'text-error' : 'text-grey-400'
              )}>
                {isOverdue ? (
                  <AlertCircle className="w-3 h-3" />
                ) : (
                  <Calendar className="w-3 h-3" />
                )}
                {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
              </span>
            ) : null}
          </div>
        </div>

        {/* Actions menu - appears on hover */}
        <TaskActions onEdit={onEdit} onDelete={onDelete} onDuplicate={onDuplicate} />
        {contextMenu && (
          <TaskContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onClose={() => setContextMenu(null)}
          />
        )}
      </div>
    );
  }

  // Full layout - with borders, more padding
  return (
    <div
      ref={elementRef}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      className={cn(
        'group flex items-start gap-3 p-4 rounded-lg border bg-white border-grey-200 hover:border-primary/30 hover:shadow-sm transition-all',
        onClick && 'cursor-pointer',
        isNew && 'border-2 border-orange-400',
        className
      )}
    >
      {/* Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleComplete?.();
        }}
        disabled={isToggling}
        className={cn(
          'flex-shrink-0 w-6 h-6 rounded-full border-2 border-grey-300 hover:border-primary flex items-center justify-center transition-all mt-0.5',
          isToggling && 'opacity-50'
        )}
      />

      <div className="flex-1 min-w-0">
        {/* Title row with priority and actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <p className="font-medium text-secondary">
              {task.title}
            </p>
            {/* Status badge */}
            {task.statusConfig && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 mt-0.5"
                style={{ backgroundColor: `${task.statusConfig.color}20`, color: task.statusConfig.color }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: task.statusConfig.color }}
                />
                {task.statusConfig.name}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {(task.priority === 'HIGH' || task.priority === 'URGENT') && (
              <AlertCircle className={cn('w-4 h-4 flex-shrink-0', priority.color)} />
            )}
            <TaskActions
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          </div>
        </div>

        {/* Description */}
        {task.description && (
          <div className="mt-1 line-clamp-2">
            <RichContent html={task.description} className="text-sm text-grey-500" />
          </div>
        )}

        {/* Project info (for global tasks view) */}
        {showProjectInfo && (task.projectName || task.taskListName) && (
          <div className="flex items-center gap-2 mt-1.5 text-xs">
            {task.projectName && (
              <span className="text-grey-500 hover:text-secondary transition-colors">
                {task.projectName}
              </span>
            )}
            {task.projectName && task.taskListName && (
              <span className="text-grey-300">•</span>
            )}
            {task.taskListName && (
              <span className="text-grey-500 hover:text-secondary transition-colors">
                {task.taskListName}
              </span>
            )}
          </div>
        )}

        {/* Meta row: assignee, due date, in progress, comments */}
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          {/* Assignee */}
          {(task.assignees?.length || task.assignee) ? (
            (() => {
              const people = task.assignees?.length
                ? task.assignees
                : task.assignee
                  ? [task.assignee]
                  : [];
              return (
                <div className="flex items-center gap-1.5">
                  <span className="flex items-center -space-x-1.5">
                    {people.slice(0, 3).map((a) => (
                      <AssigneeAvatar key={a.id} person={a} className="ring-1 ring-white" />
                    ))}
                  </span>
                  <span className="text-xs text-grey-500">
                    {people.length === 1 ? people[0].name : `${people.length} assignees`}
                  </span>
                </div>
              );
            })()
          ) : (
            <span className="text-xs text-grey-400 italic">No assignee</span>
          )}

          {/* Due date */}
          {task.dueDate && (
            <div className={cn(
              'flex items-center gap-1 text-xs',
              isOverdue ? 'text-error' : 'text-grey-500'
            )}>
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {isOverdue ? 'Overdue: ' : ''}
                {new Date(task.dueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: new Date(task.dueDate).getUTCFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
                  timeZone: 'UTC',
                })}
              </span>
            </div>
          )}

          {/* In Progress toggle button - only for assignees */}
          {isAssignee && onToggleInProgress && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleInProgress();
              }}
              disabled={isUpdatingStatus}
              className={cn(
                'flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-colors',
                isInProgress
                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  : 'bg-grey-100 text-grey-500 hover:bg-grey-200',
                isUpdatingStatus && 'opacity-50'
              )}
            >
              <Clock className="w-3 h-3" />
              {isInProgress ? 'In Progress' : 'Start'}
            </button>
          )}

          {/* Show status badge for non-assignees when in progress */}
          {!isAssignee && isInProgress && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-500">
              In Progress
            </span>
          )}

          {/* Comments count */}
          {commentsCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-grey-500">
              <MessageSquare className="w-3.5 h-3.5" />
              {commentsCount}
            </div>
          )}
        </div>
      </div>
      {contextMenu && (
        <TaskContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={onEdit}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
